import ApplicationController from '../../../javascript/controllers/application_controller'

/**
 * Publication Fetch Component Controller
 *
 * Manages radio button groups for selecting old vs new values when fetching publication metadata.
 *
 * Responsibilities:
 * 1. Field Toggle: Enable/disable hidden fields based on individual radio button selection
 * 2. Select All: Bulk selection of all "old" or all "new" values across all field groups
 *
 * Usage:
 *   <form data-controller="views--publications--fetch">
 *     <!-- Select all buttons -->
 *     <button data-action="views--publications--fetch#selectOld">Keep all old</button>
 *     <button data-action="views--publications--fetch#selectNew">Apply all new</button>
 *
 *     <!-- Individual radio groups -->
 *     <input type="radio"
 *            name="field_choice[title]"
 *            value="old"
 *            data-field-name="title"
 *            data-action="change->views--publications--fetch#disable">
 *     <input type="radio"
 *            name="field_choice[title]"
 *            value="new"
 *            data-field-name="title"
 *            data-action="change->views--publications--fetch#enable">
 *     <input type="hidden"
 *            name="publication[title]"
 *            value="New Title"
 *            disabled
 *            data-views--publications--fetch-target="hiddenField"
 *            data-field-name="title">
 *   </form>
 */
export default class extends ApplicationController {
  static targets = ['hiddenField', 'bulkOldCheckbox', 'bulkNewCheckbox']

  /**
   * Enable hidden fields when "new" radio is selected
   *
   * @param {Event} event - The change event from the radio button
   */
  enable(event) {
    const fieldName = event.target.dataset.fieldName
    const matchingFields = this.hiddenFieldTargets.filter(
      (field) => field.dataset.fieldName === fieldName,
    )

    matchingFields.forEach((field) => {
      field.disabled = false
    })
  }

  /**
   * Disable hidden fields when "old" radio is selected
   *
   * @param {Event} event - The change event from the radio button
   */
  disable(event) {
    const fieldName = event.target.dataset.fieldName
    const matchingFields = this.hiddenFieldTargets.filter(
      (field) => field.dataset.fieldName === fieldName,
    )

    matchingFields.forEach((field) => {
      field.disabled = true
    })
  }

  /**
   * Select All Old: Triggered by "Keep all old" checkbox
   * When checked, uncheck the "new" checkbox (mutual exclusivity) and select all old radios
   *
   * @param {Event} event - The change event from the checkbox
   */
  selectOld(event) {
    if (event.target.checked) {
      // Uncheck the "Apply all new" checkbox
      if (this.hasBulkNewCheckboxTarget) {
        this.bulkNewCheckboxTarget.checked = false
      }

      // Select all old values
      this.selectAllWithValue('old')
    }
  }

  /**
   * Select All New: Triggered by "Apply all new" checkbox
   * When checked, uncheck the "old" checkbox (mutual exclusivity) and select all new radios
   *
   * @param {Event} event - The change event from the checkbox
   */
  selectNew(event) {
    if (event.target.checked) {
      // Uncheck the "Keep all old" checkbox
      if (this.hasBulkOldCheckboxTarget) {
        this.bulkOldCheckboxTarget.checked = false
      }

      // Select all new values
      this.selectAllWithValue('new')
    }
  }

  /**
   * Select all radio buttons with the specified value and trigger their change events
   *
   * @param {string} value - The value to select ('old' or 'new')
   * @private
   */
  selectAllWithValue(value) {
    // Find all radio buttons within this controller's scope that match the field_choice pattern
    const radios = this.element.querySelectorAll('input[type="radio"][name^="field_choice["]')

    // Group radios by their name attribute to handle each group separately
    const radioGroups = this.groupRadiosByName(radios)

    // For each radio group, select the radio with the target value
    Object.values(radioGroups).forEach((group) => {
      const targetRadio = group.find((radio) => radio.value === value)
      if (targetRadio && !targetRadio.checked) {
        targetRadio.checked = true
        // Dispatch change event so the toggle() method can respond and enable/disable hidden fields
        targetRadio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  /**
   * Group radio buttons by their name attribute
   *
   * @param {NodeList} radios - Collection of radio input elements
   * @returns {Object} - Object with name as key and array of radios as value
   * @private
   */
  groupRadiosByName(radios) {
    const groups = {}

    radios.forEach((radio) => {
      const name = radio.name
      if (!groups[name]) {
        groups[name] = []
      }
      groups[name].push(radio)
    })

    return groups
  }

  /**
   * Uncheck only the "Old value" bulk checkbox
   * Called when user manually selects a "new" radio (leaving "all old" mode)
   */
  uncheckBulkOld() {
    if (this.hasBulkOldCheckboxTarget) {
      this.bulkOldCheckboxTarget.checked = false
    }
  }

  /**
   * Uncheck only the "New value" bulk checkbox
   * Called when user manually selects an "old" radio (leaving "all new" mode)
   */
  uncheckBulkNew() {
    if (this.hasBulkNewCheckboxTarget) {
      this.bulkNewCheckboxTarget.checked = false
    }
  }
}
